import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { Button, Card, CardBody, CardFooter, Col, Input, Label, Row, Form } from 'reactstrap';
import * as Yup from 'yup';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import apiGlobal from 'global/api.global';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import FormLoading from 'Components/FormLoading';
import ErrorTooltip from 'Components/ErrorTooltip';
import env from 'environment_system/env_system';
import { checkValidObject, handleServerResponse, saveEditForm, useSafeFormikContext } from 'GenericForms/Helper';
import { RootState } from 'index';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentReportAction } from 'Store/Generic/ReportingSlice';
interface EconFormType {
    toggleTab?: (value: number) => void,
    activeTab?: number,
    setErrorMessage: any,
    editReportBool: boolean
}

const OtherDetailsComponent = ({
    toggleTab,
    activeTab,
    setErrorMessage,
    editReportBool
}: EconFormType) => {
    /** State variables start*/
    const { VesselID, ReportID, VoyageID, CurrentReport, loading } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const econshipDeckAdditionalInfoObj = CurrentReport?.data?.deck_officer?.econship_deck_officer_additional ?? null;
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    /** State variables end*/

    /** Assign initial values to formik object */
    const initialValues = (() => {
        let obj: any;
        if (editValues?.deck_officer?.econship_deck_officer_additional) {
            return editValues?.deck_officer?.econship_deck_officer_additional;
        }
        if (checkValidObject(econshipDeckAdditionalInfoObj)) {
            if (econshipDeckAdditionalInfoObj?.display === true) {
                obj = { ...econshipDeckAdditionalInfoObj };
                return econshipDeckAdditionalInfoObj;
            }
        } else {
            obj = {
                avg_speed: null,
                total_distance_covered: null,
                fore_draft: null,
                aft_draft: null,
                any_stoppages: false,
                reason_for_stoppage: null,
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
            };
            return obj;
        }
    })();

    /** Validation schema for formik object */
    const validationSchema = Yup.object().shape({
        avg_speed: Yup.string()
            .matches(
                /^\d{0,3}(?:\.\d{1,4})?$/,
                `${commonValidationMessages.before3after4}`
            ).nullable(),
        total_distance_covered: Yup.string()
            .matches(
                /^(?!0\d)(\d{1,7}|0)(\.\d{1,4})?$/,
                commonValidationMessages.before7after4
            ).nullable(),
        fore_draft: Yup.string()
            .matches(
                /^\d{0,2}(?:\.\d{1,4})?$/,
                commonValidationMessages.before2after4
            ).nullable(),
        aft_draft: Yup.string()
            .matches(
                /^\d{0,2}(?:\.\d{1,4})?$/,
                commonValidationMessages.before2after4
            ).nullable(),
        reason_for_stoppage: Yup.string().when("any_stoppages", (value: any, schema: any) => {
            return value[0] === true ? schema.required(commonValidationMessages.required) : schema.nullable();
        })
    });

    const handleSubmit = async (values: any, actions: any) => {
        const responseArray: any = [];
        if (env?.form_validation === false) {
            setErrorMessage(null)
            toggleTab(activeTab + 1);
        }
        actions.setSubmitting(true);
        if (checkValidObject(econshipDeckAdditionalInfoObj)) {
            responseArray.push(apiGlobal.put(`/econship_deck_officer_additional/${econshipDeckAdditionalInfoObj.id}/`, values));
        }
        else {
            responseArray.push(apiGlobal.post(`/econship_deck_officer_additional/`, values));
        }
        await handleServerResponse(responseArray).then(async (res) => {
            if (res === true) {
                dispatch(getCurrentReportAction(VesselID, ReportID));
                if (env?.form_validation === true) {
                    setErrorMessage(null)
                    toggleTab(activeTab + 1);
                }
            } else {
                setErrorMessage(res)
            }
            actions.setSubmitting(false);
        })
        actions.setSubmitting(false);
    };

    const EconDeckDetailsFormik: any = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: handleSubmit
    });

    useEffect(() => {
        if (editInitialValues?.deck_officer) {
            setEditValues({
                ...editValues,
                deck_officer: {
                    ...editValues?.deck_officer,
                    econship_deck_officer_additional: EconDeckDetailsFormik?.values,
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [EconDeckDetailsFormik?.values])

    return (
        <Card className='mb-0 border-0'>
            {loading && <FormLoading />}
            {!loading &&
                <Form autoComplete="off" onSubmit={EconDeckDetailsFormik.handleSubmit}>
                    <CardBody className='p-2'>
                        <Row className="mb-2">
                            <Col sm={3}>
                                <div className="form-group">
                                    <Label className="mb-0" htmlFor="avg_speed">Average Speed (since COSP)</Label>
                                    <div className="input-group mb-3">
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="text"
                                            autoFocus={true}
                                            {...EconDeckDetailsFormik}
                                            id={`avg_speed`}
                                            onChange={(e) => EconDeckDetailsFormik.setFieldValue('avg_speed', e.target.value)}
                                            name={`avg_speed`}
                                            className="max-width-7 text-right form-control"
                                            defaultValue={EconDeckDetailsFormik.values.avg_speed || ""}
                                        />
                                        <div className="input-group-text round_border">
                                            knots
                                        </div>
                                    </div>
                                    {EconDeckDetailsFormik.errors?.avg_speed && EconDeckDetailsFormik.touched?.avg_speed &&
                                        EconDeckDetailsFormik.touched?.avg_speed &&
                                        EconDeckDetailsFormik.errors?.avg_speed && env?.form_validation === true && (
                                            <ErrorTooltip
                                                target={`avg_speed`}
                                                message={EconDeckDetailsFormik.errors?.avg_speed}
                                                open={EconDeckDetailsFormik.errors?.avg_speed}
                                            />
                                        )}
                                </div>
                            </Col>
                            <Col sm={3}>
                                <div className="form-group">
                                    <Label className="mb-0" htmlFor="total_distance_covered">Total distance covered (since COSP)</Label>
                                    <div className="input-group mb-3">
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="text"
                                            id={`total_distance_covered`}
                                            name={`total_distance_covered`}
                                            className="max-width-7 text-right form-control"
                                            defaultValue={EconDeckDetailsFormik.values.total_distance_covered || ""}
                                            onChange={(e) => EconDeckDetailsFormik.setFieldValue('total_distance_covered', e.target.value)}
                                        />
                                        <div className="input-group-text round_border">
                                            nm
                                        </div>
                                    </div>
                                    {EconDeckDetailsFormik.errors?.total_distance_covered && EconDeckDetailsFormik.touched?.total_distance_covered &&
                                        EconDeckDetailsFormik.touched?.total_distance_covered &&
                                        EconDeckDetailsFormik.errors?.total_distance_covered && env?.form_validation === true && (
                                            <ErrorTooltip
                                                target={`total_distance_covered`}
                                                message={EconDeckDetailsFormik.errors?.total_distance_covered}
                                                open={EconDeckDetailsFormik.errors?.total_distance_covered}
                                            />
                                        )}
                                </div>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            <h6>Arrival Drafts Next Port</h6>
                            <Col sm={3}>
                                <div className="form-group">
                                    <Label className="mb-0" htmlFor="fore_draft">Fore draft</Label>
                                    <div className="input-group mb-3">
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="text"
                                            id={`fore_draft`}
                                            className={`form-control max-width-7 text-right`}
                                            name='fore_draft'
                                            defaultValue={EconDeckDetailsFormik.values.fore_draft || ""}
                                            onChange={(e) => EconDeckDetailsFormik.setFieldValue('fore_draft', e.target.value)}
                                        />
                                        <div className="input-group-text round_border">
                                            m
                                        </div>
                                    </div>
                                    {EconDeckDetailsFormik.errors?.fore_draft && EconDeckDetailsFormik.touched?.fore_draft &&
                                        EconDeckDetailsFormik.touched?.fore_draft &&
                                        EconDeckDetailsFormik.errors?.fore_draft && env?.form_validation === true && (
                                            <ErrorTooltip
                                                target={`fore_draft`}
                                                message={EconDeckDetailsFormik.errors?.fore_draft}
                                                open={EconDeckDetailsFormik.errors?.fore_draft}
                                            />
                                        )}
                                </div>
                            </Col>
                            <Col sm={3}>
                                <div className="form-group">
                                    <Label className="mb-0" htmlFor="aft_draft">Aft draft</Label>
                                    <div className="input-group mb-3">
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="text"
                                            id={`aft_draft`}
                                            className={`form-control max-width-7 text-right`}
                                            name={`aft_draft`}
                                            defaultValue={EconDeckDetailsFormik.values.aft_draft || ""}
                                            onChange={(e) => EconDeckDetailsFormik.setFieldValue('aft_draft', e.target.value)}
                                        />
                                        <div className="input-group-text round_border">
                                            m
                                        </div>
                                    </div>
                                    {EconDeckDetailsFormik.errors?.aft_draft && EconDeckDetailsFormik.touched?.aft_draft &&
                                        EconDeckDetailsFormik.touched?.aft_draft &&
                                        EconDeckDetailsFormik.errors?.aft_draft && env?.form_validation === true && (
                                            <ErrorTooltip
                                                target={`aft_draft`}
                                                message={EconDeckDetailsFormik.errors?.aft_draft}
                                                open={EconDeckDetailsFormik.errors?.aft_draft}
                                            />
                                        )}
                                </div>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            <Col sm={4}>
                                <div className="d-flex flex-wrap gap-2">
                                    <Label className="mb-0" htmlFor="any_stoppages">Any stoppages since last report ? </Label>
                                    <div className="square-switch sqswitch">
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="checkbox"
                                            switch="none"
                                            name={`any_stoppages`}
                                            id={`any_stoppages`}
                                            onChange={(e: any) => EconDeckDetailsFormik.setFieldValue('any_stoppages', e.target.checked)}
                                            defaultChecked={EconDeckDetailsFormik.values?.any_stoppages}
                                        />
                                        <Label
                                            htmlFor="any_stoppages"
                                            data-on-label='Yes'
                                            data-off-label='No'
                                        >
                                        </Label>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6}>
                                {EconDeckDetailsFormik.values.any_stoppages && (
                                    <div className="form-group">
                                        <Label className="mb-0" htmlFor="reason_for_stoppage">
                                            Please provide reason for stoppage
                                        </Label>
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="textarea"
                                            className="form-control"
                                            id="reason_for_stoppage"
                                            name="reason_for_stoppage"
                                            value={EconDeckDetailsFormik.values.reason_for_stoppage || ""}
                                            onChange={EconDeckDetailsFormik.handleChange}
                                            onBlur={EconDeckDetailsFormik.handleBlur}
                                        />
                                    </div>
                                )}
                                {EconDeckDetailsFormik?.errors.reason_for_stoppage &&
                                    EconDeckDetailsFormik?.touched?.reason_for_stoppage &&
                                    env?.form_validation === true && (
                                        <ErrorTooltip
                                            target="reason_for_stoppage"
                                            message={EconDeckDetailsFormik?.errors.reason_for_stoppage}
                                            open={!!EconDeckDetailsFormik?.errors.reason_for_stoppage}
                                        />
                                    )}
                            </Col>
                        </Row>
                    </CardBody>
                    <CardFooter className="p-2">
                        <Row className="ele_row1">
                            <div className="d-flex flex-wrap gap-5">
                                <Button
                                    type={editReportBool ? "button" : "submit"}
                                    color="primary"
                                    className="btn_size_cstm pos-end"
                                    disabled={EconDeckDetailsFormik.isSubmitting || editIsSubmitting}
                                    onClick={async () => {
                                        const validationErrors = await EconDeckDetailsFormik?.validateForm();
                                        Object.keys(EconDeckDetailsFormik?.values || {}).forEach((key: any) => {
                                            EconDeckDetailsFormik?.setFieldTouched(key, true);
                                        });
                                        if (editReportBool && Object.keys(validationErrors).length === 0) {
                                            await saveEditForm(editSubmit, toggleTab, activeTab);
                                        }
                                    }}
                                >
                                    {(EconDeckDetailsFormik.isSubmitting || editIsSubmitting) && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}
                                    Next <i className="bx bx-chevron-right ms-1" />
                                </Button>
                                <Button
                                    type="button"
                                    color="primary"
                                    className="btn_size_cstm "
                                    onClick={() => {
                                        EconDeckDetailsFormik?.setErrors({});
                                        toggleTab(activeTab - 1);
                                        setErrorMessage(null)
                                    }}
                                >
                                    <i className="bx bx-chevron-left me-1" /> Previous
                                </Button>
                            </div>
                        </Row>
                    </CardFooter>
                    <Row className='mt-2'>
                        <FormValuesDebug values={[EconDeckDetailsFormik?.values, EconDeckDetailsFormik?.errors, initialValues]} />
                    </Row>
                </Form>
            }
        </Card >
    );
};

export default OtherDetailsComponent;
